import { CommonModule } from '@angular/common';
import { Component, OnInit, computed, effect, signal } from '@angular/core';
import { TooltipModule } from 'primeng/tooltip';

import { FeedbackQualityData } from '../../../../models/workspace-overview.model';
import { WorkspaceOverviewStateService } from '../../../../services/workspace-overview-state.service';
import { WorkspaceOverviewService } from '../../../../services/workspace-overview.service';

/** SVG ring radius (px). The circumference is 2π × radius. */
const RING_RADIUS = 42;
const RING_CIRCUMFERENCE = 2 * Math.PI * RING_RADIUS;

type StarType = 'full' | 'half' | 'empty';

/**
 * Feedback & Quality Widget (admin-only)
 *
 * Positive sentiment ring, NPS, average rating (stars), Bob thumbs-up rate,
 * dismiss rate, and per-feature horizontal bars.
 *
 * Template binds:
 *  - `ringRadius` — SVG `r` attribute
 *  - `ringCircumference` — SVG `stroke-dasharray` attribute
 *  - `ringOffset()` — computed SVG `stroke-dashoffset`
 *  - `stars()` — array of `'full'|'half'|'empty'` star type strings
 *
 * Reads state from `WorkspaceOverviewStateService`. Mounted only when
 * `state.showFeedbackQuality()` is true (container guard).
 *
 * Tooltip: "Displays feedback provided to Bob and the quality of Bob's
 * responses in a given period".
 */
@Component({
    selector: 'app-feedback-quality-widget',
    standalone: true,
    imports: [CommonModule, TooltipModule],
    templateUrl: './feedback-quality-widget.component.html',
    styleUrl: './feedback-quality-widget.component.scss',
})
export class FeedbackQualityWidgetComponent implements OnInit {
    readonly data = signal<FeedbackQualityData | null>(null);
    readonly loading = signal(false);

    /** Exposed to the template for the SVG `r` attribute. */
    readonly ringRadius = RING_RADIUS;

    /** Exposed to the template for the SVG `stroke-dasharray` attribute. */
    readonly ringCircumference = RING_CIRCUMFERENCE;

    /** Computed SVG `stroke-dashoffset` — shrinks the arc to match sentiment %. */
    readonly ringOffset = computed(() => {
        const pct = this.data()?.positiveSentimentPct ?? 0;
        return RING_CIRCUMFERENCE * (1 - pct / 100);
    });

    /**
     * Returns a 5-element array of `'full'|'half'|'empty'` strings so the
     * template can render a star-rating row with `*ngFor`.
     * Half-star threshold: ≥ 0.25 above the floor.
     */
    readonly stars = computed<StarType[]>(() => {
        const rating = this.data()?.avgRating ?? 0;
        return Array.from({ length: 5 }, (_, i) => {
            const diff = rating - i;
            if (diff >= 0.75) return 'full';
            if (diff >= 0.25) return 'half';
            return 'empty';
        });
    });

    constructor(
        private readonly service: WorkspaceOverviewService,
        private readonly state: WorkspaceOverviewStateService,
    ) {
        effect(() => {
            this.fetch(this.state.viewMode(), this.state.period(), this.state.effectiveRange());
        });
    }

    ngOnInit(): void { /* initial fetch triggered by the effect above */ }

    private fetch(
        _mode: ReturnType<WorkspaceOverviewStateService['viewMode']>,
        period: ReturnType<WorkspaceOverviewStateService['period']>,
        range: ReturnType<WorkspaceOverviewStateService['effectiveRange']>,
    ): void {
        this.loading.set(true);
        this.service.getFeedbackQuality({ mode: _mode, period, range }).subscribe({
            next: data => { this.data.set(data); this.loading.set(false); },
            error: ()   => this.loading.set(false),
        });
    }
}
